"use client";

import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Alert } from "../alert";
import { Knopf } from "../knopf";
import { SchliessenSymbol } from "../modal";
import { HornautFigur, type HornautFigurZustand } from "../hornaut-figur";
import { HORNAUT_KOPF_AVATAR } from "../hornaut-kopf";

/*
 * Hornaut-Begleiter der Tour (Musterkatalog M-018, Design-Guide `hornaut-begleiter.md` und `tour-overlay.md`).
 * Reiner Darstellungs-Baustein: Fragen, Antworten und Zustand liegen beim Aufrufer (`apps/web/src/components/preline-tour-host.tsx`),
 * der Baustein kennt weder Registry noch Netzwerk. Erste Lieferung OHNE Modell: die Antworten sind feste Erklärtexte zum
 * Schritt; die Zustände „lädt“, „Fehler“ und „nicht verfügbar“ sind vorgesehen, werden ohne Modell aber nicht ausgelöst.
 *
 * Figur (Betreiber 09.10.2026 „A, Stufe 1 bauen“, AW-018): die Figur ist Marke und trägt selbst kein `.ai-gated`; sie bewegt sich
 * (`HornautFigur`): „spricht“ kurz, wenn der Schritt wechselt (`kontext` ändert sich), „zeigt“ einmalig bei Schritten mit Ziel
 * (`schrittMitZiel`), sonst ruhig. Im Panel steht sie im gated Rahmen (das Panel ist die Modell-Funktion „Fragen“).
 *
 * R-D: Panel und Knopf tragen `.ai-gated` (+ `data-ki="gated"`), der Auslöser zusätzlich `.ai-mark`. Bei KI aus
 * (`[data-ai="off"]`, Rolle ∧ Policy) gibt der Aufrufer die Hornaut-Teile gar nicht erst aus; die Tour bleibt
 * vollständig (Fallback). Esc im Panel schließt zuerst das Panel (`tour-overlay.tsx` fängt Esc und ruft `beiPanelSchliessen`).
 *
 * Maße: ab 768 px rechts am Rand (400 px, `w-100`), darunter Bottom-Sheet. Angedockt (die Seite rückt, ab 1280 px) oder
 * überlagert (768 bis 1279 px) entscheidet der Aufrufer; die Fläche des Panels ist in beiden Fällen gleich.
 * Abweichung AW-015a: das Panel dockt ab 1280 px statt wie der Assistent-Guide erst ab 2xl (1536 px).
 * Abweichung AW-015c: die Figur-Datei (Marke) gilt wie geliefert, ohne Freigabe durch Claude Design.
 */

// HORNAUT_KOPF_AVATAR liegt seit W104 A gemeinsam in `../hornaut-kopf` (Import oben).

/**
 * Ganzkörper-Figur (Marke): der Baustein `HornautFigur` (`../hornaut-figur.tsx`, mit Bewegung). Diese Hülle bleibt für
 * `tour-overlay.tsx` (Einleitung, Abschluss) bestehen und reicht Zustand und Erscheinen durch; ohne Angabe: ruhig.
 * Die Figur ist Marke und nicht `.ai-gated` (Abweichung AW-018); ob sie im Aufrufer in einem gated Rahmen steht, entscheidet dieser.
 */
export function HornautFigurBild({
  hoehe,
  className,
  zustand,
  erscheinen,
  onZustandEnde,
}: {
  hoehe: number;
  className?: string;
  zustand?: HornautFigurZustand;
  erscheinen?: boolean;
  onZustandEnde?: () => void;
}) {
  return <HornautFigur hoehe={hoehe} className={className} zustand={zustand} erscheinen={erscheinen} onZustandEnde={onZustandEnde} />;
}

/** Lucide `Sparkles` (KI-Kennzeichnung, R-F). */
export function KiSymbol({ className }: { className?: string }) {
  return (
    <svg className={className ?? "shrink-0 size-4"} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
      <path d="M4 17v2" />
      <path d="M5 18H3" />
    </svg>
  );
}

export interface HornautFrage {
  id: string;
  /** Beschriftung des Chips, zugleich die „Frage“ der Person im Gespräch. */
  frage: string;
}

/** `leer` = Chips und Figur, `antwort` = Frage und feste Antwort, `laedt`/`fehler`/`gesperrt`: vorgesehen, ohne Modell nicht ausgelöst. */
export type HornautZustand = "leer" | "laedt" | "antwort" | "fehler" | "gesperrt";

/** Darstellung: Bottom-Sheet (unter 768 px), überlagert (768 bis 1279 px) oder angedockt (ab 1280 px). */
export type HornautModus = "sheet" | "ueberlagert" | "angedockt";

export const HORNAUT_PANEL_BREITE = 400;

const CHIP =
  "py-1.5 px-3 inline-flex items-center text-xs font-medium rounded-full border border-layer-line bg-layer text-layer-foreground hover:bg-layer-hover focus:outline-hidden focus:bg-layer-focus disabled:opacity-50 disabled:pointer-events-none";

/**
 * Rand rechts (md+) bzw. Bottom-Sheet; z-91 über Dimmer und Karte der Tour (z-90), unter der Leiste „Zur Tour“ (z-92). Mobil endet das
 * Sheet mit `bottom-14` über der Leiste (h-14, `tour-overlay.tsx`), beide stehen nebeneinander, die Leiste bleibt bedienbar.
 */
const FLAECHE =
  "ai-gated fixed z-91 flex flex-col bg-popover border-popover-line shadow-md motion-reduce:transition-none max-md:inset-x-0 max-md:bottom-14 max-md:h-135 max-md:max-h-5/6 max-md:rounded-t-xl max-md:border md:inset-y-0 md:end-0 md:w-100 md:border-s";

export function HornautBegleiter({
  modus,
  kontext,
  fragen,
  gestellt,
  antwort,
  zustand,
  beiFrage,
  beiSchliessen,
  beiErneut,
  schrittMitZiel = false,
}: {
  /** Der aktuelle Schritt zeigt auf ein Ziel: die Figur zeigt einmalig (sonst „spricht“ sie kurz beim Schrittwechsel). */
  schrittMitZiel?: boolean;
  modus: HornautModus;
  /** Kontext-Chip, z. B. „Kapitel 1 · Schritt 2 von 8: Projekte“. */
  kontext: string;
  /** Alle Fragen des Schritts (Chips). */
  fragen: HornautFrage[];
  /** Id der gestellten Frage; `null` im Zustand leer. */
  gestellt: string | null;
  /** Feste Antwort zur gestellten Frage. */
  antwort: string | null;
  zustand: HornautZustand;
  beiFrage: (id: string) => void;
  beiSchliessen: () => void;
  /** Nur Zustand „Fehler“: Frage wiederholen. */
  beiErneut?: () => void;
}) {
  const id = useId();
  const wurzel = useRef<HTMLElement>(null);
  // Erst nach dem Einhängen rendern (das Portal braucht `document`).
  const [eingehaengt, setEingehaengt] = useState(false);
  useEffect(() => setEingehaengt(true), []);

  // Figur-Zustand: beim Schrittwechsel (`kontext`) kurz „spricht“ (2,5 s) bzw. bei Ziel einmalig „zeigt“; sonst ruhig. Der erste
  // Lauf (Öffnen des Panels) bleibt ruhig. Die Zeitmarke setzt auch bei motion-reduce zurück (dort feuert animationend nicht).
  const [figur, setFigur] = useState<HornautFigurZustand>("ruhig");
  const erstesMal = useRef(true);
  useEffect(() => {
    if (erstesMal.current) {
      erstesMal.current = false;
      return;
    }
    setFigur(schrittMitZiel ? "zeigt" : "spricht");
    const t = setTimeout(() => setFigur("ruhig"), schrittMitZiel ? 1500 : 2500);
    return () => clearTimeout(t);
    // Nur der Schrittwechsel löst aus; `schrittMitZiel` gehört zum selben Schritt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kontext]);

  // Fokus: beim Öffnen auf den ersten Chip (sonst den Schließen-Knopf); die Rückgabe an den Knopf macht `tour-overlay.tsx`.
  useEffect(() => {
    if (!eingehaengt || !wurzel.current) return;
    const ziel = wurzel.current.querySelector<HTMLElement>("[data-hornaut-chip]") ?? wurzel.current.querySelector<HTMLElement>("[data-hornaut-schliessen]");
    ziel?.focus({ preventScroll: true });
  }, [eingehaengt]);

  if (!eingehaengt) return null;

  const frageText = fragen.find((f) => f.id === gestellt)?.frage ?? null;
  const weitere = fragen.filter((f) => f.id !== gestellt);
  const mobil = modus === "sheet";

  return createPortal(
    <>
      {/* Preline: communication/communication-sub-chat-inbox/communication-cat-support-chat-sidebars/slide-in-support-chat-sidebar-with-reply-composer (Hülle; Chips nach help-topics-widget-with-suggested-prompts, Blasen nach ai-chat-bubbles; Eigenbau, Inhalt eigen) */}
      <aside
        ref={wurzel}
        className={FLAECHE}
        role={mobil ? "dialog" : "complementary"}
        aria-label="Hornaut, Begleiter der Tour"
        data-ki="gated"
        data-ki-aktiv="an"
        data-hornaut-begleiter={modus}
      >
        <div className="py-3 px-4 flex items-center justify-between gap-x-2 border-b border-line-2">
          <div className="min-w-0 flex items-center gap-x-2">
            {/* eslint-disable-next-line @next/next/no-img-element -- statisches Kopf-Bild der Marke */}
            <img src={HORNAUT_KOPF_AVATAR} alt="" aria-hidden="true" width={32} height={32} className="shrink-0 size-8 rounded-full object-cover ring-1 ring-ki-line bg-ki-soft" />
            <span className="min-w-0">
              <span id={`${id}-titel`} className="block truncate font-semibold text-sm leading-4 text-foreground">
                Hornaut
              </span>
              <span className="block truncate text-xs text-muted-foreground-1 leading-4">begleitet dich durch die Tour</span>
            </span>
          </div>
          <Knopf variante="ghost" nurSymbol groesse="sm" starr label="Hornaut schließen" symbol={<SchliessenSymbol />} onClick={beiSchliessen} daten={{ "data-hornaut-schliessen": true }} />
        </div>

        <div className="grow min-h-0 overflow-y-auto pt-3 pb-5 px-4 space-y-4" aria-live="polite">
          <span className="inline-flex max-w-full items-center gap-x-1.5 py-1.5 px-3 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
            <span className="truncate">{kontext}</span>
          </span>
          <p className="text-xs text-muted-foreground-1">
            Du bekommst Erklärungen zu diesem Schritt. Es wird nichts gesendet, ausgewertet oder gespeichert.
          </p>

          {zustand === "leer" && (
            <div className="space-y-4">
              <div className="flex items-end gap-x-3">
                <HornautFigur hoehe={150} zustand={figur} erscheinen onZustandEnde={() => setFigur("ruhig")} />
                <p className="mb-2 text-sm text-foreground bg-layer border border-layer-line rounded-xl py-2 px-3">Frag mich, wenn dir an diesem Schritt etwas unklar ist.</p>
              </div>
              <FragenChips fragen={fragen} beiFrage={beiFrage} beschriftungId={`${id}-fragen`} />
            </div>
          )}

          {zustand !== "leer" && frageText && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <p className="max-w-xs text-sm text-foreground break-words bg-primary-100 rounded-xl py-2 px-3">{frageText}</p>
              </div>
              {zustand === "laedt" && (
                <div className="flex gap-x-2" role="status">
                  <span className="sr-only">Der Hornaut antwortet.</span>
                  <div className="w-full max-w-xs bg-layer border border-layer-line rounded-xl py-3 px-3 space-y-2 animate-pulse motion-reduce:animate-none" aria-hidden="true">
                    <div className="h-2 rounded-full bg-muted-focus" />
                    <div className="h-2 w-2/3 rounded-full bg-muted-focus" />
                  </div>
                </div>
              )}
              {zustand === "antwort" && antwort && (
                <div className="flex gap-x-2">
                  {/* eslint-disable-next-line @next/next/no-img-element -- statisches Kopf-Bild der Marke */}
                  <img src={HORNAUT_KOPF_AVATAR} alt="" aria-hidden="true" width={32} height={32} className="shrink-0 mt-auto size-8 rounded-full object-cover ring-1 ring-ki-line bg-ki-soft" />
                  <div className="min-w-0 max-w-xs">
                    <p className="mb-1.5 ps-2.5 text-xs text-muted-foreground">Hornaut</p>
                    <p className="bg-layer border border-layer-line rounded-xl py-2 px-3 text-sm text-foreground break-words" data-hornaut-antwort>
                      {antwort}
                    </p>
                  </div>
                </div>
              )}
              {zustand === "fehler" && (
                <Alert ton="warnung">
                  Der Hornaut konnte gerade nicht antworten. Bitte erneut versuchen.
                  {beiErneut && (
                    <span className="mt-2 block">
                      <Knopf onClick={beiErneut}>Erneut fragen</Knopf>
                    </span>
                  )}
                </Alert>
              )}
              {zustand === "gesperrt" && <Alert ton="warnung">Der Hornaut steht gerade nicht zur Verfügung. Die Tour läuft ganz normal weiter.</Alert>}
              {zustand === "antwort" && weitere.length > 0 && <FragenChips fragen={weitere} beiFrage={beiFrage} beschriftungId={`${id}-weitere`} titel="Weitere Fragen" />}
            </div>
          )}
        </div>

        <div className="py-2 px-4 border-t border-line-2 bg-muted text-xs text-muted-foreground-1">
          Feste Erklärtexte zu diesem Schritt, keine Antworten eines Sprachmodells.
        </div>
      </aside>
    </>,
    document.body,
  );
}

function FragenChips({ fragen, beiFrage, beschriftungId, titel }: { fragen: HornautFrage[]; beiFrage: (id: string) => void; beschriftungId: string; titel?: string }) {
  return (
    <div>
      <span className="block mb-2 text-xs text-muted-foreground-1" id={beschriftungId}>
        {titel ?? "Das kannst du mich fragen"}
      </span>
      <div className="flex flex-wrap gap-1 sm:gap-2" role="group" aria-labelledby={beschriftungId}>
        {fragen.map((f) => (
          <button key={f.id} type="button" className={CHIP} onClick={() => beiFrage(f.id)} data-hornaut-chip={f.id}>
            {f.frage}
          </button>
        ))}
      </div>
    </div>
  );
}

